import apiClient from './api';
import {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentDetail,
  AgentPerformanceMetrics,
  AgentDocument,
  PaginatedResponse,
} from '../types';

// File upload error messages
const FILE_UPLOAD_ERRORS = {
  INVALID_TYPE: 'รองรับเฉพาะไฟล์ PDF, JPG, PNG เท่านั้น',
  FILE_TOO_LARGE: 'ขนาดไฟล์ใหญ่เกินไป (สูงสุด 10MB)',
  UPLOAD_FAILED: 'อัพโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  DOWNLOAD_FAILED: 'ดาวน์โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  DELETE_FAILED: 'ลบไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
};

// Allowed file types and max size
const ALLOWED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

class AgentService {
  private readonly basePath = '/agents';

  /**
   * Get list of agents with pagination and search
   * Enhanced with comprehensive error handling and logging
   */
  async getAgents(
    skip: number = 0,
    limit: number = 50,
    search?: string,
    teamId?: string,
    isActive?: boolean,
    availableForTeam?: boolean
  ): Promise<PaginatedResponse<Agent>> {
    try {
      console.log('[AgentService] Fetching agents...', {
        skip,
        limit,
        search,
        teamId,
        isActive,
        availableForTeam,
        timestamp: new Date().toISOString(),
      });

      const params: any = { skip, limit };
      if (search) {
        params.search = search;
      }
      if (teamId) {
        params.team_id = teamId;
      }
      if (isActive !== undefined) {
        params.is_active = isActive;
      }
      if (availableForTeam !== undefined) {
        params.available_for_team = availableForTeam;
      }

      const response = await apiClient.get<PaginatedResponse<Agent>>(
        this.basePath,
        { params }
      );

      // Validate response structure first
      if (!response.data) {
        console.error('[AgentService] Invalid response: missing data');
        const validationError: any = new Error('ไม่สามารถโหลดข้อมูลตัวแทนได้: ข้อมูลไม่ถูกต้อง');
        validationError.isValidationError = true;
        throw validationError;
      }

      if (!response.data.items || !Array.isArray(response.data.items)) {
        console.error('[AgentService] Invalid response structure:', {
          hasItems: !!response.data.items,
          isArray: Array.isArray(response.data.items),
          data: response.data,
        });
        const validationError: any = new Error('ไม่สามารถโหลดข้อมูลตัวแทนได้: โครงสร้างข้อมูลไม่ถูกต้อง');
        validationError.isValidationError = true;
        throw validationError;
      }

      console.log('[AgentService] Agents fetched successfully:', {
        count: response.data.items.length,
        total: response.data.total,
        skip: response.data.skip,
        limit: response.data.limit,
        timestamp: new Date().toISOString(),
      });

      return response.data;
    } catch (error: any) {
      console.error('[AgentService] Failed to fetch agents:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });

      // If it's a validation error, rethrow it as-is
      if (error.isValidationError) {
        throw error;
      }

      // Handle different error types
      if (!error.response) {
        // Network error
        throw new Error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
      }

      const status = error.response.status;
      const errorMessage = error.response?.data?.detail || error.response?.data?.message;

      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          throw new Error('กรุณาเข้าสู่ระบบใหม่อีกครั้ง');
        case 403:
          // Forbidden - no permission
          throw new Error('คุณไม่มีสิทธิ์เข้าถึงข้อมูลตัวแทน');
        case 404:
          // Not found
          throw new Error('ไม่พบข้อมูลตัวแทน');
        case 500:
        case 502:
        case 503:
          // Server error
          throw new Error('เกิดข้อผิดพลาดที่เซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง');
        default:
          // Other errors
          throw new Error(
            errorMessage || 'ไม่สามารถโหลดข้อมูลตัวแทนได้ กรุณาลองใหม่อีกครั้ง'
          );
      }
    }
  }

  /**
   * Get agents available for team assignment
   * Convenience method that filters for agents without teams and active status
   */
  async getAvailableAgents(
    skip: number = 0,
    limit: number = 100
  ): Promise<PaginatedResponse<Agent>> {
    return this.getAgents(skip, limit, undefined, undefined, undefined, true);
  }

  /**
   * Get single agent by ID with performance metrics
   */
  async getAgent(id: string, includeQuotations: boolean = true, includePolicies: boolean = true): Promise<AgentDetail> {
    const params: any = {};
    if (includeQuotations) {
      params.include_quotations = true;
    }
    if (includePolicies) {
      params.include_policies = true;
    }
    
    const response = await apiClient.get<AgentDetail>(`${this.basePath}/${id}`, { params });
    return response.data;
  }

  /**
   * Create new agent with enhanced fields
   * Includes license types, structured address, and other new fields
   */
  async createAgent(data: AgentCreate): Promise<Agent> {
    const response = await apiClient.post<Agent>(this.basePath, data);
    return response.data;
  }

  /**
   * Update existing agent with enhanced fields
   * Includes license types, structured address, and other new fields
   */
  async updateAgent(id: string, data: AgentUpdate): Promise<Agent> {
    const response = await apiClient.put<Agent>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete agent (soft delete)
   */
  async deleteAgent(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Get agent performance metrics
   */
  async getAgentPerformance(id: string): Promise<AgentPerformanceMetrics> {
    const response = await apiClient.get<AgentPerformanceMetrics>(
      `${this.basePath}/${id}/performance`
    );
    return response.data;
  }

  /**
   * Validate file before upload
   */
  private validateFile(file: File): void {
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !ALLOWED_FILE_TYPES.includes(fileExtension)) {
      throw new Error(FILE_UPLOAD_ERRORS.INVALID_TYPE);
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(FILE_UPLOAD_ERRORS.FILE_TOO_LARGE);
    }
  }

  /**
   * Upload document for agent
   * Validates file type (PDF, JPG, PNG) and size (max 10MB) before upload
   * @throws Error if file validation fails or upload fails
   */
  async uploadDocument(agentId: string, file: File): Promise<AgentDocument> {
    try {
      // Validate file before upload
      this.validateFile(file);

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<{ message: string; document: AgentDocument }>(
        `${this.basePath}/${agentId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      // Backend returns { message, document }, extract the document
      return response.data.document;
    } catch (error: any) {
      // If it's a validation error, rethrow it
      if (error.message === FILE_UPLOAD_ERRORS.INVALID_TYPE || 
          error.message === FILE_UPLOAD_ERRORS.FILE_TOO_LARGE) {
        throw error;
      }
      
      // For other errors, throw a generic upload failed message
      console.error('Upload error:', error);
      throw new Error(FILE_UPLOAD_ERRORS.UPLOAD_FAILED);
    }
  }

  /**
   * List documents for agent
   * Returns all non-deleted documents associated with the agent
   */
  async listDocuments(agentId: string): Promise<AgentDocument[]> {
    const response = await apiClient.get<{ agent_id: string; total: number; documents: AgentDocument[] }>(
      `${this.basePath}/${agentId}/documents`
    );
    // Backend returns { agent_id, total, documents }, extract the documents array
    return response.data.documents;
  }

  /**
   * Download document
   * Retrieves and triggers browser download for the specified document
   * @throws Error if download fails
   */
  async downloadDocument(agentId: string, documentId: string): Promise<void> {
    try {
      const response = await apiClient.get(
        `${this.basePath}/${agentId}/documents/${documentId}/download`,
        {
          responseType: 'blob',
        }
      );
      
      // Debug: Log all headers
      console.log('Response headers:', response.headers);
      console.log('Content-Disposition:', response.headers['content-disposition']);
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'document';
      if (contentDisposition) {
        // Try multiple patterns to extract filename
        
        // Pattern 1: filename*=utf-8''encoded-filename (RFC 5987 - URL encoded)
        let filenameMatch = contentDisposition.match(/filename\*=utf-8''([^;]+)/i);
        if (filenameMatch) {
          // Decode the URL-encoded filename
          filename = decodeURIComponent(filenameMatch[1]);
          console.log('Extracted filename (RFC 5987):', filename);
        } else {
          // Pattern 2: filename="filename.ext" (with quotes)
          filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
            console.log('Extracted filename (quoted):', filename);
          } else {
            // Pattern 3: filename=filename.ext (without quotes)
            filenameMatch = contentDisposition.match(/filename=([^;]+)/);
            if (filenameMatch) {
              filename = filenameMatch[1].trim();
              console.log('Extracted filename (unquoted):', filename);
            }
          }
        }
      } else {
        console.warn('No Content-Disposition header found');
      }
      
      console.log('Final filename:', filename);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw new Error(FILE_UPLOAD_ERRORS.DOWNLOAD_FAILED);
    }
  }

  /**
   * View document in new tab
   * Fetches document with authentication and opens in new tab
   * @throws Error if view fails
   */
  async viewDocument(agentId: string, documentId: string): Promise<void> {
    try {
      const response = await apiClient.get(
        `${this.basePath}/${agentId}/documents/${documentId}/view`,
        {
          responseType: 'blob',
        }
      );
      
      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      
      // Clean up the URL after a delay to allow the browser to load it
      if (newWindow) {
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        // If popup was blocked, clean up immediately
        window.URL.revokeObjectURL(url);
        throw new Error('กรุณาอนุญาตให้เปิดหน้าต่างใหม่ในเบราว์เซอร์');
      }
    } catch (error) {
      console.error('View error:', error);
      if (error instanceof Error && error.message.includes('กรุณาอนุญาต')) {
        throw error;
      }
      throw new Error('ไม่สามารถเปิดไฟล์ได้ กรุณาลองใหม่อีกครั้ง');
    }
  }

  /**
   * Delete document (soft delete)
   * Marks the document as deleted without removing the physical file
   * @throws Error if delete fails
   */
  async deleteDocument(agentId: string, documentId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${this.basePath}/${agentId}/documents/${documentId}`
      );
    } catch (error) {
      console.error('Delete error:', error);
      throw new Error(FILE_UPLOAD_ERRORS.DELETE_FAILED);
    }
  }

  async downloadImportTemplate(): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/import-template`, { responseType: 'blob' });
    _downloadBlob(response.data, 'agent_import_template.xlsx');
  }

  async importFromFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/export`, { params, responseType: 'blob' });
    _downloadBlob(response.data, 'agents_export.xlsx');
  }

  async retryImportRow(rawData: Record<string, unknown>): Promise<any> {
    const formData = new FormData();
    const headers  = Object.keys(rawData).join(',');
    const values   = Object.values(rawData).map(v => String(v ?? '')).join(',');
    const blob     = new Blob([`${headers}\n${values}`], { type: 'text/csv' });
    formData.append('file', blob, 'retry.csv');
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.results?.[0] ?? response.data;
  }
}

function _downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const agentService = new AgentService();
export default agentService;
